import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { Observable, Subscription, of } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  finalize,
  map,
  switchMap,
  tap,
} from 'rxjs/operators';
import { ToasterService } from '@purespectrum1/ui/toaster-service';
import { FeotClockService } from '../qa-tools-service/feot-clock.service';
import {
  FeotClockMode,
  IFeotClockRequest,
  IFeotClockResponse,
  IFeotClockSnapshot,
  IFeotQuotaSnapshot,
} from '../qa-tools-service/feot-clock.interface';
import {
  IPathProjection,
  IProjectionInputs,
  IQuotaComparison,
  IQuotaInputs,
  compareProjections,
  projectPath,
} from './feot-projection';
import {
  buildProjectionInputs,
  buildQuotaInputs,
  quotaLabel,
  readSnapshotPath,
} from './feot-snapshot';
import {
  COMPLETES_UNAVAILABLE_HINT,
  DEFAULT_ELAPSED,
  DEFAULT_ELAPSED_UNIT,
  ELAPSED_OPTIONS,
  ELAPSED_UNIT_TO_HOURS,
  ELAPSED_UNITS,
  ElapsedUnit,
  FEOT_CLOCK_MODES,
  IElapsedOption,
  IElapsedUnitOption,
  IFeotClockModeOption,
  ISnapshotGroup,
  ISnapshotRow,
  LAMBDA_GROUPS,
  LAMBDA_ROWS_HINT,
  MIDWAY_NOT_SENT_HINT,
  MIDWAY_PATH_NOTE,
  NOT_SIMULABLE_WARNING,
  NO_RESPONSE_HINT,
  PROJECTION_DIFFERS_HINT,
  PROJECTION_MATCHES_HINT,
  PROJECTION_UNAVAILABLE_HINT,
  QUOTA_GROUP_NOTE,
  RESPONSE_ROWS,
  SNAPSHOT_CHANGED_HINT,
  SNAPSHOT_GROUPS,
  STANDARD_PATH_NOTE,
  STANDARD_PATH_WARNING,
  SURVEY_ID_HINT,
  SURVEY_LOOKUP_DEBOUNCE_MS,
  TIMESTAMP_FORMAT,
  TIMESTAMP_TZ,
  TRIGGER_HINT,
} from './feot-pacing-tool.constants';

const ELAPSED_MODES: FeotClockMode[] = [
  'elapsed',
  'elapsedHours',
  'surveyLaunchTS',
];

const HUNDRED = 100;

@Component({
  selector: 'ps-feot-pacing-tool',
  templateUrl: './feot-pacing-tool.component.html',
  styleUrls: ['./feot-pacing-tool.component.scss'],
})
export class FeotPacingToolComponent implements OnInit, OnDestroy {
  form!: UntypedFormGroup;
  modes: IFeotClockModeOption[] = FEOT_CLOCK_MODES;
  elapsedOptions: IElapsedOption[] = ELAPSED_OPTIONS;
  elapsedUnits: IElapsedUnitOption[] = ELAPSED_UNITS;
  snapshotGroups: ISnapshotGroup[] = SNAPSHOT_GROUPS;
  lambdaGroups: ISnapshotGroup[] = LAMBDA_GROUPS;
  responseRows: ISnapshotRow[] = RESPONSE_ROWS;
  timestampFormat: string = TIMESTAMP_FORMAT;
  timestampTz: string = TIMESTAMP_TZ;

  readonly copy = {
    surveyIdHint: SURVEY_ID_HINT,
    midwayNotSent: MIDWAY_NOT_SENT_HINT,
    trigger: TRIGGER_HINT,
    notSimulable: NOT_SIMULABLE_WARNING,
    standardPathWarning: STANDARD_PATH_WARNING,
    completesUnavailable: COMPLETES_UNAVAILABLE_HINT,
    projectionUnavailable: PROJECTION_UNAVAILABLE_HINT,
    standardPathNote: STANDARD_PATH_NOTE,
    midwayPathNote: MIDWAY_PATH_NOTE,
    projectionMatches: PROJECTION_MATCHES_HINT,
    projectionDiffers: PROJECTION_DIFFERS_HINT,
    quotaGroupNote: QUOTA_GROUP_NOTE,
    snapshotChanged: SNAPSHOT_CHANGED_HINT,
    lambdaRows: LAMBDA_ROWS_HINT,
    noResponse: NO_RESPONSE_HINT,
  };

  response: IFeotClockResponse | null = null;
  submitted: boolean = false;

  /** Collapsed Before/After groups, keyed by ISnapshotGroup.key. */
  collapsedGroups: Record<string, boolean> = {};

  /** Stored state read straight from the survey, before and after the update. */
  surveyBefore: IFeotClockSnapshot | null = null;
  surveyAfter: IFeotClockSnapshot | null = null;
  surveyLoading: boolean = false;
  surveyError: string | null = null;

  private _modeSubscription?: Subscription;
  private _surveySubscription?: Subscription;

  constructor(
    private _fb: UntypedFormBuilder,
    private _feotClockService: FeotClockService,
    private _toastr: ToasterService
  ) {}

  ngOnInit(): void {
    this.form = this._fb.group({
      surveyId: [null, Validators.required],
      mode: ['elapsed', Validators.required],
      elapsed: [DEFAULT_ELAPSED],
      elapsedHours: [null],
      elapsedUnit: [DEFAULT_ELAPSED_UNIT],
      surveyLaunchTS: [null],
      trigger: [true],
      sendMidwayFlag: [false],
      isMidwaySurvey: [true],
    });

    this._enableOnlySelectedModeControl(this.form.controls.mode.value);
    this._modeSubscription = this.form.controls.mode.valueChanges.subscribe(
      (mode: FeotClockMode) => this._enableOnlySelectedModeControl(mode)
    );

    this._surveySubscription = this.form.controls.surveyId.valueChanges
      .pipe(
        debounceTime(SURVEY_LOOKUP_DEBOUNCE_MS),
        map((surveyId) => Number(surveyId)),
        distinctUntilChanged(),
        tap(() => this._resetSurveyState()),
        switchMap((surveyId) => this._loadSurvey(surveyId))
      )
      .subscribe((snapshot) => (this.surveyBefore = snapshot));
  }

  ngOnDestroy(): void {
    this._modeSubscription?.unsubscribe();
    this._surveySubscription?.unsubscribe();
  }

  /**
   * The panels read the latest state there is: the After snapshot once an
   * update has run, the Before snapshot until then.
   */
  get currentSnapshot(): IFeotClockSnapshot | null {
    return this.surveyAfter || this.surveyBefore;
  }

  get isMidwayFlagSent(): boolean {
    return !!this.form?.controls.sendMidwayFlag.value;
  }

  /** What the selected value and unit add up to, shown before sending. */
  get convertedElapsedHours(): number | null {
    const raw = this.form?.controls.elapsedHours.value;
    if (raw === null || raw === undefined || raw === '') {
      return null;
    }
    return this._toHours(raw, this.form.controls.elapsedUnit.value);
  }

  /** Echoes the typed launch timestamp back, so a wrong paste is caught early. */
  get surveyLaunchTSPreview(): number | null {
    const raw = this.form?.controls.surveyLaunchTS.value;
    if (raw === null || raw === undefined || raw === '') {
      return null;
    }
    const value = Number(raw);
    return Number.isFinite(value) && value > 0 ? value : null;
  }

  get hasCompletesData(): boolean {
    return !!this.currentSnapshot?.completes;
  }

  get isMidwayEnabled(): boolean {
    return !!this.currentSnapshot?.feot.is_midway_survey;
  }

  get completesFielded(): number | null {
    return this.currentSnapshot?.completes?.fielded ?? null;
  }

  get completesGoal(): number | null {
    return this.currentSnapshot?.completes?.goal ?? null;
  }

  get completesPercent(): number | null {
    const goal = this.completesGoal;
    const fielded = this.completesFielded;
    if (fielded === null || !goal) {
      return null;
    }
    return Math.round((fielded / goal) * HUNDRED);
  }

  get remainingGoal(): number | null {
    const goal = this.completesGoal;
    const fielded = this.completesFielded;
    return goal === null || fielded === null ? null : goal - fielded;
  }

  get projectionInputs(): IProjectionInputs | null {
    return buildProjectionInputs(this.currentSnapshot);
  }

  get quotaInputs(): IQuotaInputs[] {
    return buildQuotaInputs(this.currentSnapshot);
  }

  /** The fftIntervals sum, or null against a deploy that does not send it. */
  get standardFulfilledFieldTime(): number | null {
    return (
      this.currentSnapshot?.standardFulfilledFieldTimeInPacingUnits ?? null
    );
  }

  get standardProjection(): IPathProjection | null {
    const inputs = this.projectionInputs;
    return inputs ? projectPath(inputs, this.quotaInputs, false) : null;
  }

  get midwayProjection(): IPathProjection | null {
    const inputs = this.projectionInputs;
    return inputs ? projectPath(inputs, this.quotaInputs, true) : null;
  }

  get quotaComparison(): IQuotaComparison[] {
    return compareProjections(
      this.standardProjection,
      this.midwayProjection,
      this.isMidwayEnabled
    );
  }

  /** Every active quota's prediction lands on the stored target. */
  get projectionMatchesStored(): boolean {
    const rows = this.quotaComparison;
    return rows.length > 0 && rows.every((row) => row.activeMatches);
  }

  get hasSurveyState(): boolean {
    return !!(this.surveyBefore || this.surveyError || this.surveyLoading);
  }

  get elapsedHoursNow(): number | null {
    return this.currentSnapshot?.elapsedHoursSinceLaunch ?? null;
  }

  get plannedHours(): number | null {
    return this.currentSnapshot?.plannedFieldTimeHours ?? null;
  }

  /** Can exceed 100 on an overdue survey. */
  get elapsedPercent(): number | null {
    const elapsed = this.elapsedHoursNow;
    const planned = this.plannedHours;
    if (elapsed === null || !planned) {
      return null;
    }
    return Math.round((elapsed / planned) * HUNDRED);
  }

  get beforeBarWidth(): number {
    return this.barWidth(this.surveyBefore?.elapsedHoursSinceLaunch ?? null);
  }

  get afterBarWidth(): number {
    return this.surveyAfter
      ? this.barWidth(this.surveyAfter.elapsedHoursSinceLaunch)
      : 0;
  }

  /** The payload table renders `pacing` and its interval as one row. */
  get responseView(): Record<string, unknown> | null {
    return this.response
      ? {
          ...this.response,
          pacing: `${this.response.pacing} (interval ${this.response.pacingInterval})`,
        }
      : null;
  }

  quotaLabel(quota: IFeotQuotaSnapshot): string {
    return quotaLabel(quota);
  }

  snapshotValue(
    source: object | null,
    key: string
  ): string | number | boolean | null {
    return readSnapshotPath(source, key);
  }

  /** Clamped for the bar only: the tile still shows the real percentage. */
  barWidth(hours: number | null): number {
    const planned = this.plannedHours;
    if (hours === null || !planned) {
      return 0;
    }
    return Math.min(HUNDRED, Math.max(0, (hours / planned) * HUNDRED));
  }

  toggleGroup(key: string) {
    this.collapsedGroups[key] = !this.collapsedGroups[key];
  }

  isGroupOpen(key: string): boolean {
    return !this.collapsedGroups[key];
  }

  /** A moved date gets its own highlight, so it is not read as a moved value. */
  changedClass(row: ISnapshotRow): string {
    if (!this.hasChanged(row.key)) {
      return '';
    }
    return row.isTimestamp ? 'date-changed' : 'changed';
  }

  hasChanged(key: string): boolean {
    if (!this.surveyAfter) {
      return false;
    }
    return (
      this.snapshotValue(this.surveyBefore, key) !==
      this.snapshotValue(this.surveyAfter, key)
    );
  }

  /** Re-reads the survey, so the lambda's clock can be watched after the queue drains. */
  refreshSurvey() {
    const surveyId = Number(this.form.controls.surveyId.value);
    this.surveyError = null;
    this._loadSurvey(surveyId).subscribe((snapshot) => {
      if (this.surveyAfter || this.response) {
        this.surveyAfter = snapshot;
      } else {
        this.surveyBefore = snapshot;
      }
    });
  }

  patchFeotClock() {
    this.submitted = true;
    this.response = null;

    const surveyId = Number(this.form.controls.surveyId.value);

    this._feotClockService
      .patchFeotClock(surveyId, this._buildClockRequest())
      .pipe(
        finalize(() => {
          this.submitted = false;
        })
      )
      .subscribe(
        (response: IFeotClockResponse) => {
          this.response = response;
          this._loadSurvey(surveyId).subscribe(
            (snapshot) => (this.surveyAfter = snapshot)
          );
          if (response.isMidwaySurvey) {
            this._toastr.success('FEOT clock updated');
          } else {
            this._toastr.warning(STANDARD_PATH_WARNING);
          }
        },
        (error) => {
          this._toastr.error(this._getErrorMessage(error));
        }
      );
  }

  private _buildClockRequest(): IFeotClockRequest {
    const {
      mode,
      elapsed,
      elapsedHours,
      elapsedUnit,
      surveyLaunchTS,
      trigger,
      sendMidwayFlag,
      isMidwaySurvey,
    } = this.form.getRawValue();

    const request: IFeotClockRequest = { trigger: !!trigger };

    if (mode === 'surveyLaunchTS') {
      request.surveyLaunchTS = Number(surveyLaunchTS);
    } else if (mode === 'elapsedHours') {
      request.elapsedHours = this._toHours(elapsedHours, elapsedUnit);
    } else {
      request.elapsed = elapsed;
    }

    // Tri-state: an absent key leaves the stored flag untouched, a null is rejected.
    if (sendMidwayFlag) {
      request.isMidwaySurvey = !!isMidwaySurvey;
    }

    return request;
  }

  private _resetSurveyState() {
    this.surveyBefore = null;
    this.surveyAfter = null;
    this.surveyError = null;
    this.response = null;
  }

  private _loadSurvey(surveyId: number): Observable<IFeotClockSnapshot | null> {
    if (!Number.isInteger(surveyId) || surveyId <= 0) {
      return of(null);
    }

    this.surveyLoading = true;
    return this._feotClockService.getFeotClock(surveyId).pipe(
      finalize(() => {
        this.surveyLoading = false;
      }),
      catchError((error) => {
        this.surveyError = this._getErrorMessage(error);
        return of(null);
      })
    );
  }

  private _toHours(value: number | string, unit: ElapsedUnit): number {
    return Number(value) * (ELAPSED_UNIT_TO_HOURS[unit] || 1);
  }

  /** Only the selected input stays enabled, so two of them can never be sent. */
  private _enableOnlySelectedModeControl(mode: FeotClockMode) {
    ELAPSED_MODES.forEach((name) => {
      const control = this.form.controls[name];
      const isSelected = name === mode;

      control.setValidators(isSelected ? [Validators.required] : null);

      if (isSelected) {
        control.enable({ emitEvent: false });
      } else {
        control.disable({ emitEvent: false });
      }

      control.updateValueAndValidity({ emitEvent: false });
    });
  }

  private _getErrorMessage(error: {
    error?: { error?: string; msg?: string };
    message?: string;
  }): string {
    return (
      error?.error?.error ||
      error?.error?.msg ||
      error?.message ||
      'Unknown error'
    );
  }
}
